import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';
import type { UserDetail } from '@/types/admin.types';
import {
  updateSubscription,
  toggleAdmin,
  proxyLogin,
  updateWorkerPrice,
} from '@/apis/admin';
import { QUERY_KEY } from '@/constants/queryKey';
import { ROUTER } from '@/constants/constants';
import { useAccessTokenStore, useRefreshTokenStore } from '@/store/auth';
import useCompanyInfo from '@/hooks/useCompanyInfo';

interface ActionsPanelProps {
  user: UserDetail;
}

const ActionsPanel = ({ user }: ActionsPanelProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: companyInfo } = useCompanyInfo();
  const isSuperAdmin = companyInfo?.data.isSuperAdmin || false;
  const [showUpdateInfoModal, setShowUpdateInfoModal] = useState(false);
  
  // Form state (payment method, price, admin toggle)
  const [paymentMethod, setPaymentMethod] = useState<'prepaid' | 'postpaid'>(
    user.payment_method || 'prepaid'
  );
  const [pricePerWorker, setPricePerWorker] = useState<string>(
    user.price_per_worker?.toString() || ''
  );
  const [isAdmin, setIsAdmin] = useState<boolean>(user.is_admin || false);

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (showUpdateInfoModal) {
      setPaymentMethod(user.payment_method || 'prepaid');
      setPricePerWorker(user.price_per_worker?.toString() || '');
      setIsAdmin(user.is_admin || false);
    }
  }, [showUpdateInfoModal, user]);

  const updateSubscriptionMutation = useMutation({
    mutationFn: (data: {
      user_id: number;
      payment_method?: 'prepaid' | 'postpaid';
    }) => updateSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_DETAIL, user.id]);
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_LIST]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '결제 방식 수정 실패');
    },
  });

  const toggleAdminMutation = useMutation({
    mutationFn: (data: { user_id: number }) => toggleAdmin(data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_DETAIL, user.id]);
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_LIST]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '관리자 권한 변경 실패');
    },
  });

  const proxyLoginMutation = useMutation({
    mutationFn: (data: { user_id: number }) => proxyLogin(data),
    onSuccess: (data) => {
      // Store tokens
      useAccessTokenStore.setState({ accessToken: data.data.accessToken });
      useRefreshTokenStore.setState({ refreshToken: data.data.refreshToken });
      
      toast.success('프록시 로그인 성공');
      navigate(ROUTER.DASHBOARD);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '프록시 로그인 실패');
    },
  });

  const updatePriceMutation = useMutation({
    mutationFn: (data: { price_per_worker: number }) =>
      updateWorkerPrice(user.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_DETAIL, user.id]);
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.USER_LIST]);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || '작업자 가격 수정 실패');
    },
  });

  const handlePriceChange = (value: string) => {
    // Only allow positive integers (no negative, no decimals)
    const numericValue = value.replace(/[^\d]/g, '');
    setPricePerWorker(numericValue);
  };

  const handleUpdateInfo = async () => {
    // Validate price if provided
    if (pricePerWorker && pricePerWorker.trim() !== '') {
      const priceNum = parseInt(pricePerWorker);
      if (isNaN(priceNum) || priceNum < 0) {
        toast.error('가격은 0 이상의 정수여야 합니다.');
        return;
      }
    }

    try {
      // Update payment method if changed (only allowed for free users)
      const isFreeUser = user.subscription_status === 'free';
      if (isFreeUser) {
        const currentPaymentMethod = user.payment_method || 'prepaid';
        if (paymentMethod !== currentPaymentMethod) {
          await updateSubscriptionMutation.mutateAsync({
            user_id: user.id,
            payment_method: paymentMethod,
          });
        }
      }

      // Update price if changed
      const currentPrice = user.price_per_worker?.toString() || '';
      if (pricePerWorker !== currentPrice && pricePerWorker.trim() !== '') {
        await updatePriceMutation.mutateAsync({ price_per_worker: parseInt(pricePerWorker) });
      }

      // Toggle admin if changed
      if (isAdmin !== user.is_admin) {
        await toggleAdminMutation.mutateAsync({ user_id: user.id });
      }

      toast.success('정보가 수정되었습니다.');
      setShowUpdateInfoModal(false);
    } catch (error) {
      // Error messages are handled in individual mutation onError handlers
    }
  };

  const handleProxyLogin = () => {
    if (window.confirm(`${user.company} 사용자로 프록시 로그인하시겠습니까?`)) {
      proxyLoginMutation.mutate({ user_id: user.id });
    }
  };

  const isFormChanged = () => {
    const isFreeUser = user.subscription_status === 'free';
    const paymentMethodChanged = isFreeUser && paymentMethod !== (user.payment_method || 'prepaid');
    const priceChanged = pricePerWorker !== (user.price_per_worker?.toString() || '');
    const adminChanged = isAdmin !== user.is_admin;
    return paymentMethodChanged || priceChanged || adminChanged;
  };

  const isLoading = updateSubscriptionMutation.isLoading || 
                    updatePriceMutation.isLoading || 
                    toggleAdminMutation.isLoading;

  return (
    <>
      <Container>
        <Header>
          <Text fontSize='1.25rem' fontWeight={700}>
            작업
          </Text>
        </Header>
        <ButtonRow>
          <ActionButton variant='primary' onClick={() => setShowUpdateInfoModal(true)}>
            정보 수정
          </ActionButton>
          {isSuperAdmin && (
            <ActionButton variant='secondary' onClick={handleProxyLogin}>
              프록시 로그인
            </ActionButton>
          )}
        </ButtonRow>
      </Container>

      {/* Update Info Modal */}
      {showUpdateInfoModal &&
        createPortal(
          <ModalOverlay onClick={() => setShowUpdateInfoModal(false)}>
            <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>정보 수정</ModalTitle>
            <ModalText>사용자 #{user.id} ({user.company})의 정보를 수정합니다.</ModalText>
            
            <FormField>
              <Label>결제 방식:</Label>
              <RadioGroup>
                <RadioOption>
                  <Radio
                    type='radio'
                    id='prepaid'
                    name='paymentMethod'
                    value='prepaid'
                    checked={paymentMethod === 'prepaid'}
                    onChange={(e) => setPaymentMethod(e.target.value as 'prepaid' | 'postpaid')}
                    disabled={user.subscription_status !== 'free'}
                  />
                  <RadioLabel htmlFor='prepaid' style={{ opacity: user.subscription_status !== 'free' ? 0.5 : 1 }}>
                    선불
                  </RadioLabel>
                </RadioOption>
                <RadioOption>
                  <Radio
                    type='radio'
                    id='postpaid'
                    name='paymentMethod'
                    value='postpaid'
                    checked={paymentMethod === 'postpaid'}
                    onChange={(e) => setPaymentMethod(e.target.value as 'prepaid' | 'postpaid')}
                    disabled={user.subscription_status !== 'free'}
                  />
                  <RadioLabel htmlFor='postpaid' style={{ opacity: user.subscription_status !== 'free' ? 0.5 : 1 }}>
                    후불
                  </RadioLabel>
                </RadioOption>
              </RadioGroup>
              <HelperText>
                현재: {user.payment_method === 'prepaid' ? '선불' : user.payment_method === 'postpaid' ? '후불' : '선불'}
                {user.subscription_status !== 'free' && (
                  <span style={{ color: Theme.colors.WARNING_RED, marginLeft: '0.5rem' }}>
                    (무료 상태에서만 변경 가능)
                  </span>
                )}
              </HelperText>
            </FormField>

            <FormField>
              <Label>작업자당 가격:</Label>
              <Input
                type='text'
                inputMode='numeric'
                placeholder='가격 입력 (숫자만)'
                value={pricePerWorker}
                onChange={(e) => handlePriceChange(e.target.value)}
              />
              {pricePerWorker && (
                <HelperText>
                  현재: {user.price_per_worker ? `${user.price_per_worker.toLocaleString('ko-KR')}원` : '-'}
                </HelperText>
              )}
            </FormField>

            <FormField>
              <CheckboxContainer>
                <Checkbox
                  type='checkbox'
                  id='isAdmin'
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                />
                <CheckboxLabel htmlFor='isAdmin'>
                  관리자 권한 {isAdmin ? '부여' : '해제'}
                </CheckboxLabel>
              </CheckboxContainer>
              <HelperText>
                현재: {user.is_admin ? '관리자' : '일반 사용자'}
              </HelperText>
            </FormField>

            <ModalButtonRow>
              <ModalButton
                backgroundColor={Theme.colors.G_3}
                onClick={() => setShowUpdateInfoModal(false)}
              >
                취소
              </ModalButton>
              <ModalButton
                backgroundColor={Theme.colors.B_1}
                onClick={handleUpdateInfo}
                disabled={isLoading || !isFormChanged()}
              >
                {isLoading ? '처리 중...' : '수정'}
              </ModalButton>
            </ModalButtonRow>
          </Modal>
        </ModalOverlay>,
          document.body
        )}
    </>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  min-width: 8rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease-in-out;

  ${(props) => {
    if (props.variant === 'primary') {
      return `
        background-color: ${Theme.colors.B_3};
        color: white;
        &:hover {
          background-color: ${Theme.colors.B_2};
          transform: translateY(-0.0625rem);
          box-shadow: 0 0.25rem 0.5rem rgba(19, 127, 218, 0.3);
        }
      `;
    } else {
      return `
        background-color: white;
        color: ${Theme.colors.G_1};
        border: 0.0625rem solid ${Theme.colors.G_3};
        &:hover {
          background-color: ${Theme.colors.G_6};
          border-color: ${Theme.colors.G_2};
          transform: translateY(-0.0625rem);
        }
      `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  min-width: 25rem;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled(Text)`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const ModalText = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const FormField = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_2};
  font-weight: 600;
`;

const HelperText = styled(Text)`
  font-size: 0.75rem;
  color: ${Theme.colors.G_2};
  margin-top: -0.25rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Radio = styled.input`
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  accent-color: ${Theme.colors.B_3};
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  cursor: pointer;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${Theme.colors.B_1};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  accent-color: ${Theme.colors.B_3};
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  cursor: pointer;
  font-weight: 500;
`;

const ModalButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const ModalButton = styled.button<{ backgroundColor: string }>`
  padding: 0.625rem 1.25rem;
  background-color: ${(props) => props.backgroundColor};
  color: white;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ActionsPanel;
